import React, { useEffect, useState, useCallback, Suspense } from "react";
import { Grid, Box, useMediaQuery, Theme, Container } from "@mui/material";
import { useParams, useLocation } from "react-router-dom";
import ReactPlayer from "react-player/dailymotion";
import { Helmet } from "react-helmet-async";
import { connect } from "react-redux";
import { debounce } from "lodash";

import LoadingDetails from "@components/LoadingDetails";
import Analytics from "@containers/Analytics";
import Content from "./components/Content";

import type {
  ILanguage,
  IMetaConfig,
  IResizerConfig,
  IListingPage,
} from "@ducks/config/types";
import type { IBreadcrumb } from "@components/Breadcrumbs";
import type { IItem } from "@ducks/listing/types";
import type { AppState } from "@store";

import {
  interpolate,
  isPromotion,
  typeOfIntegration,
  isIntegrationNeeded,
} from "@lib/common";
import { resetCouponStatus, resetRedeemCoupon } from "@ducks/coupon";
import { REDEEMTYPE, INTEGRATION_TYPE } from "@ducks/listing/types";
import { resizeImage, CONTENT_IMAGETYPE } from "@lib/resizer";
import { getDetails, resetDetails } from "@ducks/listing";
import { ICouponStatusState } from "@ducks/coupon/types";
import { trackShowItem, ITEMSTATUS, trackLink } from "@analytics";
import { PORTAL, isProd } from "@ducks/config";
import settings from "../../settings";

const Overhead = React.lazy(() => import("./components/Overhead"));
const ErrorOverlay = React.lazy(() => import("@components/ErrorOverlay"));
const FlashSale = React.lazy(() => import("@components/FlashSale"));
const RedeemBar = React.lazy(() => import("@containers/RedeemBar"));
const ImageSlider = React.lazy(() => import("@components/ImageSlider"));
const SideContent = React.lazy(() => import("./components/SideContent"));
const GWEChat = React.lazy(() => import("@components/GWEChat"));

interface IStateProps {
  listingLoading: boolean;
  listingSuccess: boolean;
  sideContentLoading: boolean;
  allItems: IItem[];
  details: IItem | null;
  error: boolean;
  success: boolean;
  language: ILanguage;
  meta: IMetaConfig;
  resizer: IResizerConfig;
  listing: IListingPage;
  status: ICouponStatusState;
}

interface IDetailsProps extends IStateProps {
  getDetails: (code: string, items: IItem[]) => void;
  resetDetails: () => void;
  resetCouponStatus: () => void;
  resetRedeemCoupon: () => void;
}

const Details: React.FC<IDetailsProps> = (props) => {
  const { code } = useParams<{ code: string }>();
  const {
    listingLoading,
    listingSuccess,
    allItems,
    details,
    error,
    success,
    language,
    getDetails,
    resetDetails,
    resetCouponStatus,
    resetRedeemCoupon,
    meta,
    resizer,
    listing,
    sideContentLoading,
    status,
  } = props;

  const [showRedemption, setShowRedemption] = useState<boolean | null>(null);
  const [showSmartCardSelector, setShowSmartCardSelector] =
    useState<boolean>(false);
  const [showGweChat, setShowGweChat] = useState<boolean>(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [breadcrumbs, setBreadcrumbs] = useState<IBreadcrumb[]>([]);

  //Get current pathname using useLocation. Might need if more children are required in breadcrumb.
  const location = useLocation();

  const videoControls = listing.videoControls;

  const url = success && details ? videoControls?.url + details?.videoUrl : "";

  const isMobile = useMediaQuery<Theme>(({ breakpoints }) =>
    breakpoints.down("md")
  );

  const throttledViewEventPush = useCallback(
    debounce((status: ITEMSTATUS) => trackShowItem(details, status), 1000),
    [details]
  );

  useEffect(() => {
    if (listingSuccess && code) getDetails(code, allItems);
    return (): void => resetDetails();
  }, [listingSuccess, code, allItems, getDetails, resetDetails]);

  // To only call on unmount
  useEffect(() => {
    return (): void => {
      resetCouponStatus();
      resetRedeemCoupon();
    };
  }, []);

  useEffect(() => {
    if (details && !details.codeNotRequired) {
      if (
        isIntegrationNeeded(details) &&
        typeOfIntegration(details) === INTEGRATION_TYPE.iqiyi
      ) {
        setShowRedemption(true);
        setShowSmartCardSelector(false);
      } else {
        setShowRedemption(true);
        setShowSmartCardSelector(true);
      }
    } else if (details?.termsLink) {
      setShowRedemption(true);
      setShowSmartCardSelector(false);
    } else {
      setShowSmartCardSelector(false);
    }

    if (details) {
      const itemStatus = details.hasExpired
        ? ITEMSTATUS.expired
        : details.hasStarted
        ? ITEMSTATUS.available
        : ITEMSTATUS.starting;
      throttledViewEventPush(itemStatus);
      if (isPromotion(details)) {
        setShowGweChat(details.enableGwe);
      }
    }
  }, [
    details,
    setShowRedemption,
    setShowGweChat,
    throttledViewEventPush,
    setShowSmartCardSelector,
  ]);

  useEffect(() => {
    if (details && success) {
      const tempBreadcrumbs: IBreadcrumb[] = [];
      tempBreadcrumbs.push({
        type: details.type,
        name: details.title,
        url: location.pathname,
      });
      setBreadcrumbs(tempBreadcrumbs);
    }
  }, [details, setBreadcrumbs]);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  });

  const renderError = (): React.ReactNode => (
    <Suspense fallback={null}>
      <ErrorOverlay
        title={language.PAGE_NOT_FOUND_TITLE}
        message={language.PAGE_NOT_FOUND_MESSAGE}
        buttonText={language.BACK_TO_HOME}
        buttonUrl="/"
      />
    </Suspense>
  );

  const renderMeta = (): React.ReactNode => {
    if (!details) return null;

    const title = interpolate(meta.detailsTitle, { title: details.title });
    const url = `${settings[PORTAL].webUrl}${location.pathname.substring(1)}`;
    const description = interpolate(meta.detailsDesc, { description: details.highlight });

    return (
      <Helmet prioritizeSeoTags>
        <title>{title}</title>

        {isProd && details.isVisibleOnListing ? (
          <meta name="robots" content="index,follow" />
        ) : (
          <meta name="robots" content="noindex,nofollow" />
        )}

        <link rel="canonical" href={url} />
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={meta.thumbnail} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content={meta.detailsOGType} />
        <meta name="twitter:card" content={meta.twitterCard} />
        <meta name="twitter:url" content={url} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={meta.thumbnail} />
        <meta name="twitter:site" content={meta.twitterSite} />
      </Helmet>
    );
  };
  const [message, setMessage] = useState<string | React.ReactNode>("");

  useEffect(() => {
    if (details && isPromotion(details)) {
      constructPromotionsMessage();
    }
  }, [status]);

  const constructPromotionsMessage = useCallback(() => {
    setMessage("");
    if (details) {
      const { codeNotRequired, hasStarted, hasExpired } = details;

      const { error, success, reason } = status;

      if (hasExpired) {
        return;
      } else if (!hasStarted) {
        return;
      } else if (!codeNotRequired) {
        if (error) {
          return;
        } else if (success && reason) {
          return;
        } else if (success) {
          setMessage(
            details && details.termsLink && (
              <>
                <a
                  href={details.termsLink}
                  target="_blank"
                  rel="noopener"
                  onClick={(): void => {
                    trackLink(
                      details,
                      details.termsLink,
                      language.details.TERMS_AND_CONDITIONS
                    );
                  }}
                >
                  {language.details.TERMS_AND_CONDITIONS}
                </a>
                &nbsp;{language.details.APPLY}
              </>
            )
          );
          throttledViewEventPush(ITEMSTATUS.available);
        }
      } else {
        setMessage(
          details && details.termsLink && (
            <>
              <a
                href={details.termsLink}
                target="_blank"
                rel="noopener"
                onClick={(): void => {
                  trackLink(
                    details,
                    details.termsLink,
                    language.details.TERMS_AND_CONDITIONS
                  );
                }}
              >
                {language.details.TERMS_AND_CONDITIONS}
              </a>
              &nbsp;{language.details.APPLY}
            </>
          )
        );
        throttledViewEventPush(ITEMSTATUS.available);
      }
    }
  }, [details, setMessage, status, throttledViewEventPush]);

  return (
    <Box display="flex" alignItems="center" flexDirection="column">
      <Analytics details={details} />

      <Grid
        container
        spacing={0}
        maxWidth={'xl'}
        sx={({ breakpoints }) => ({
          justifyContent: 'center',
          [breakpoints.up('xl')]: {
            px: 9,
            pt: 6
          },
          [breakpoints.down('xl')]: {
            px: 2,
            pb: details?.exploreLatestPromotion ? 3 : 0
          }
        })}
      >
        <Grid item xl={8} lg={8} md={12} sm={12} xs={12}>
          <Box
            sx={({ breakpoints }) => ({
              '& .MuiContainer-root': { padding: 0 },
              [breakpoints.up('lg')]: {
                maxWidth: 752
              }
            })}
          >
            {listingLoading && (
              <Grid container spacing={0} justifyContent="center">
                <Grid item xs={12} md={12}>
                  <LoadingDetails />
                </Grid>
              </Grid>
            )}

            {/*
            Hidden for now https://astrogo.atlassian.net/browse/ACM22-4656
            {success && (
              <Suspense fallback={null}>
                <Overhead
                  breadcrumbs={breadcrumbs}
                  showSmartCardSelector={showSmartCardSelector}
                />
              </Suspense>
            )} */}

            {!success && error && renderError()}

            {success && details && (
              <Box sx={{ mt: 0 }}>
                {renderMeta()}
                <Container
                  sx={({ breakpoints }) => ({
                    [breakpoints.down('md')]: {
                      width: screenWidth,
                      mx: -2
                    }
                  })}
                >
                  <Grid container spacing={0} justifyContent="center">
                    <Grid item xl={12} md={12} sm={12} xs={12}>
                      <Box
                        sx={{
                          height: 'auto',
                          position: 'relative',
                          aspectRatio: '16/9',
                          maxWidth: { lg: '752px' }
                        }}
                      >
                        {details.imageUrl && !details.videoUrl && (
                          <Suspense fallback={null}>
                            <ImageSlider
                              imageUrl={resizeImage(
                                details.imageUrl || '',
                                resizer,
                                CONTENT_IMAGETYPE.detail
                              )}
                              sx={{
                                borderRadius: isMobile ? 0 : undefined,
                                width: {
                                  xs: '100%',
                                  lg: '752px'
                                }
                              }}
                              name={details.name}
                            />
                          </Suspense>
                        )}

                        {details.videoUrl && ReactPlayer.canPlay(url) && (
                          <Box
                            sx={{
                              borderRadius: {
                                xs: 0,
                                sm: '1rem'
                              },
                              width: '100%',
                              height: '100%',
                              overflow: 'hidden'
                            }}
                          >
                            <ReactPlayer
                              pip
                              controls
                              url={url}
                              width="100%"
                              height="100%"
                              stopOnUnmount={false}
                              muted={videoControls.muted}
                              playing={videoControls.playing}
                              config={{
                                params: {
                                  'ui-logo': videoControls.uiLogo,
                                  'ui-start-screen-info': videoControls.uiStartScreenInfo,
                                  playlist: videoControls.playlist,
                                  'sharing-enable': videoControls.sharingEnable
                                }
                              }}
                            />
                          </Box>
                        )}
                        {details.redeemType === REDEEMTYPE.online && details.isFlashSale && (
                          <Suspense fallback={null}>
                            <FlashSale start={details.redemptionFrom} />
                          </Suspense>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Container>

                <Container>
                  <Grid container spacing={0} justifyContent="center">
                    <Grid item md={12} xs={12}>
                      <Content details={details} />
                      {message && (
                        <Box
                          sx={() => ({
                            flexGrow: 1,
                            fontWeight: 'bold',
                            lineHeight: '20px',
                            '& a': {
                              color: ({ palette }) => palette.primary.main
                            }
                          })}
                        >
                          <span style={{ fontSize: '16px' }} id="message">
                            {message}
                          </span>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Container>
              </Box>
            )}
          </Box>
        </Grid>

        {details?.exploreLatestPromotion && !sideContentLoading && (
          <Grid
            item
            xl={4}
            lg={4}
            md={12}
            sm={12}
            xs={12}
            display={'flex'}
            alignItems={'flex-start'}
            justifyContent={'flex-end'}
          >
            <Box
              sx={({ breakpoints }) => ({
                width: '100%',
                [breakpoints.up('xl')]: {
                  maxWidth: 336
                },
                [breakpoints.down('sm')]: {
                  mt: 2
                }
              })}
            >
              <Suspense fallback={null}>
                <SideContent />
              </Suspense>
            </Box>
          </Grid>
        )}
      </Grid>

      {showGweChat && (
        <Suspense fallback={null}>
          <GWEChat {...settings.gwe} />
        </Suspense>
      )}

      {showRedemption && success && !error && (
        <Suspense fallback={null}>
          <RedeemBar throttledViewEventPush={throttledViewEventPush} />
        </Suspense>
      )}
    </Box>
  );
};

Details.defaultProps = {
  listingLoading: true,
  listingSuccess: false,
  details: null,
  allItems: [],
  error: false,
  success: false,
  getDetails: (): undefined => undefined,
  resetDetails: (): undefined => undefined,
  resetCouponStatus: (): undefined => undefined,
  resetRedeemCoupon: (): undefined => undefined,
};

const mapStateToProps = (state: AppState): IStateProps => ({
  listingLoading: state.listing.loading,
  sideContentLoading: state.sideContent.loading,
  listingSuccess: state.listing.success,
  details: state.listing.details,
  allItems: state.listing.allItems,
  error: state.listing.detailsError,
  success: state.listing.detailsSuccess,
  language: state.config.language,
  meta: state.config.meta,
  resizer: state.config.resizer,
  listing: state.config.listing,
  status: state.coupon.status,
});

const mapDispatchToProps = {
  getDetails,
  resetDetails,
  resetCouponStatus,
  resetRedeemCoupon,
};

export default connect(mapStateToProps, mapDispatchToProps)(Details);
